/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useEffect } from "react";
import { Outlet, useSearchParams, useLocation } from "react-router-dom";
import Header from "./components/common/Header";
import Footer from "./components/common/Footer";
import "./App.css";
import { useTranslation } from "react-i18next";
import PerformanceMonitor from "./components/performance/PerformanceMonitor";
import CookieConsent from "./components/common/CookieConsent";
import "@fontsource-variable/inter";

import { initWebVitals } from "./utils/performance";

initWebVitals();
const App: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const isStandalone = searchParams.get("standalone") === "true";
  // Auth pages (/auth and all sub-routes) are self-contained — they must not
  // render the site header or footer. Detect by stripping any language prefix
  // (e.g. /de/auth/callback → /auth/callback) before comparing.
  const pathWithoutLang = location.pathname.replace(/^\/(en|de|fr|es|pt|it)/, "");
  const isAuthPage = pathWithoutLang.startsWith("/auth");

  // Keep the document language in sync with the active locale on every page,
  // including standalone (embedded) views that do not render the header. The
  // header otherwise sets <html lang> via Helmet, but standalone pages skip it,
  // leaving screen readers and search engines with the wrong language.
  useEffect(() => {
    if (i18n.language) {
      document.documentElement.lang = i18n.language;
    }
  }, [i18n.language]);

  return (
    <div className="page">
      {!isStandalone && !isAuthPage && (
        <a href="#main-content" className="skip-to-content">
          {t("accessibility.skip_to_content")}
        </a>
      )}
      {!isStandalone && !isAuthPage && <Header />}

      {/* Performance monitor (only visible in development) */}
      <PerformanceMonitor />

      {/* GDPR: CookieConsent gates Google Analytics. Shows once until
          the user accepts or declines. See CookieConsent.tsx for details. */}
      <CookieConsent />

      <main id="main-content">
        <Outlet />
      </main>
      {!isStandalone && !isAuthPage && <Footer />}
    </div>
  );
};

export default App;
